/**
 * 关节编辑纯逻辑（P0-6c）：把 25 个语义 controls 组织成分组，并提供 rig 的不可变更新器。
 *
 * 为什么单独成模块（而非塞进页面）：滑杆分组、值钳制、rig 更新都是可离线断言的纯计算，
 * 与 cameraMath/poseCatalog 同一分层原则——UI 只渲染与转发。
 *
 * 数据流：CharacterRig { posePresetId, controls, boneOverrides } 经 model/pose 的
 * composeBones 合成最终 65 骨欧拉角，再由 poseApply.applyBoneMap 施加到模型。
 * 本模块只负责 rig 的读写变换，不碰 fetch / 不碰渲染。
 */
import { Euler, Quaternion } from 'three';

import {
  clampDegrees,
  type BoneMap,
  type CharacterRig,
  type ControlName,
} from '../../model/pose';
import { STD_FBX_REST } from '../../model/stdFbxRest';
import { BONE_NAMES } from '../../model/rigConstants';

export interface ControlDef {
  name: ControlName;
  /** UI 标签，如「抬臂」「前倾」 */
  label: string;
  min: number;
  max: number;
}

export interface ControlGroup {
  title: string;
  controls: ControlDef[];
}

/** 全 ±180 的通用范围；弯曲/张合类在 UI 上够用，标定（D-01）只调轴序与正负不改范围 */
const FULL = { min: -180, max: 180 } as const;
/** 关节弯曲类（肘/膝）生理上单向，给 0–180 更顺手 */
const BEND = { min: 0, max: 180 } as const;

/**
 * 按肢体分组的滑杆布局（对齐竞品"身体/躯干/头/四肢"的调节习惯）。
 * 覆盖 CONTROL_NAMES 全 25 项，测试断言"每项恰好出现一次"。
 */
export const CONTROL_GROUPS: readonly ControlGroup[] = [
  {
    title: '身体',
    controls: [
      { name: 'body.pitch', label: '前倾', ...FULL },
      { name: 'body.yaw', label: '转身', ...FULL },
      { name: 'body.roll', label: '侧倾', ...FULL },
      { name: 'torso.pitch', label: '躯干俯仰', ...FULL },
      { name: 'torso.yaw', label: '躯干扭转', ...FULL },
      { name: 'torso.roll', label: '躯干侧倾', ...FULL },
      { name: 'head.pitch', label: '头俯仰', ...FULL },
      { name: 'head.yaw', label: '头转向', ...FULL },
      { name: 'head.roll', label: '头侧倾', ...FULL },
    ],
  },
  {
    title: '左臂',
    controls: [
      { name: 'leftShoulder.pitch', label: '抬臂', ...FULL },
      { name: 'leftShoulder.spread', label: '张臂', ...FULL },
      { name: 'leftShoulder.twist', label: '旋臂', ...FULL },
      { name: 'leftElbow.bend', label: '肘弯', ...BEND },
    ],
  },
  {
    title: '右臂',
    controls: [
      { name: 'rightShoulder.pitch', label: '抬臂', ...FULL },
      { name: 'rightShoulder.spread', label: '张臂', ...FULL },
      { name: 'rightShoulder.twist', label: '旋臂', ...FULL },
      { name: 'rightElbow.bend', label: '肘弯', ...BEND },
    ],
  },
  {
    title: '左腿',
    controls: [
      { name: 'leftHip.pitch', label: '抬腿', ...FULL },
      { name: 'leftHip.spread', label: '张腿', ...FULL },
      { name: 'leftHip.twist', label: '旋腿', ...FULL },
      { name: 'leftKnee.bend', label: '膝弯', ...BEND },
    ],
  },
  {
    title: '右腿',
    controls: [
      { name: 'rightHip.pitch', label: '抬腿', ...FULL },
      { name: 'rightHip.spread', label: '张腿', ...FULL },
      { name: 'rightHip.twist', label: '旋腿', ...FULL },
      { name: 'rightKnee.bend', label: '膝弯', ...BEND },
    ],
  },
];

/** 所有滑杆定义（铺平，供"重置全部"等遍历） */
export function allControlDefs(): ControlDef[] {
  return CONTROL_GROUPS.flatMap((g) => g.controls);
}

/** 空 rig（新角色或彻底重置时用） */
export function emptyRigState(): CharacterRig {
  return { rigType: 'mixamo', posePresetId: null, controls: {}, boneOverrides: {} };
}

/** 规整控件值：整数化 + 钳到 ±180（与库内 controls 全整数一致） */
function normalizeDeg(deg: number): number {
  return Math.round(clampDegrees(deg));
}

/**
 * 设置单个关节控制值，返回新 rig（不可变；原对象不动，供 React 依赖比较）。
 * 值 0 视为"无偏移"，从字典删除而非存 0，保持 controls 稀疏、便于工程文件瘦身。
 */
export function withControl(rig: CharacterRig, name: ControlName, deg: number): CharacterRig {
  const controls = { ...rig.controls };
  const v = normalizeDeg(deg);
  if (v === 0) {
    delete controls[name];
  } else {
    controls[name] = v;
  }
  return { ...rig, controls };
}

/** 设定预设姿势 id（保留当前关节微调；换预设通常应配合 clearControls 由调用方决定） */
export function withPreset(rig: CharacterRig, presetId: string | null): CharacterRig {
  return { ...rig, posePresetId: presetId };
}

/** 清空所有关节微调（回到预设原样） */
export function clearControls(rig: CharacterRig): CharacterRig {
  return { ...rig, controls: {} };
}

/** 读取某控件当前值（缺失=0） */
export function controlValue(rig: CharacterRig | undefined, name: ControlName): number {
  return rig?.controls[name] ?? 0;
}

/** XYZ 欧拉（弧度）→四元数（与 applyBoneMap 同一 'XYZ' 序） */
export function eulerToQuat(e: readonly [number, number, number]): Quaternion {
  return new Quaternion().setFromEuler(new Euler(e[0], e[1], e[2], 'XYZ'));
}

/**
 * 反解（P0-6d 逐骨 gizmo 的地基）：gizmo 把骨拧到 GLB 局部四元数 boneQuat 后，
 * 求应写进 boneOverrides 的 FBX 局部欧拉。
 * 与 applyBoneMap 正向严格互逆：正向 `q = qRest ∘ qStd⁻¹ ∘ qPose` → 反向 `qPose = qStd ∘ qRest⁻¹ ∘ q`。
 * 不互逆就会重现 D-36a 那类坐标系错乱。
 */
export function boneQuatToOverride(
  boneQuat: Quaternion,
  restQuat: Quaternion,
  std: readonly [number, number, number, number],
): [number, number, number] {
  const qStd = new Quaternion(std[0], std[1], std[2], std[3]);
  // qStd ∘ restQuat⁻¹ ∘ boneQuat（multiply 改宿主，qStd 为局部新对象可安改；rest 先 clone 不伤入参）
  const qPose = qStd.multiply(restQuat.clone().invert()).multiply(boneQuat);
  const e = new Euler().setFromQuaternion(qPose.normalize(), 'XYZ');
  return [e.x, e.y, e.z];
}

/**
 * 设置单骨覆盖（gizmo 写入路径），返回新 rig（不可变）。
 * 接近零的欧拉视为"无覆盖"→删除，保持 boneOverrides 稀疏。
 */
export function withOverride(
  rig: CharacterRig,
  bone: string,
  euler: readonly [number, number, number],
): CharacterRig {
  const boneOverrides = { ...rig.boneOverrides };
  if (euler.every((x) => Math.abs(x) < 1e-5)) {
    delete boneOverrides[bone];
  } else {
    boneOverrides[bone] = [euler[0], euler[1], euler[2]];
  }
  return { ...rig, boneOverrides };
}

/** 清空所有单骨覆盖（回到预设+滑杆层） */
export function clearOverrides(rig: CharacterRig): CharacterRig {
  return { ...rig, boneOverrides: {} };
}

/* ---------- 逐骨列表（P0-6d 路线 B：右栏选骨 → 视口旋转环） ---------- */

const BONE_LABEL_ZH: Record<string, string> = {
  Hips: '骨盆', Spine: '脊柱', Spine1: '脊柱1', Spine2: '胸椎',
  Neck: '颈部', Head: '头部', HeadTop_End: '头顶',
  LeftShoulder: '左肩', LeftArm: '左上臂', LeftForeArm: '左前臂', LeftHand: '左手掌',
  RightShoulder: '右肩', RightArm: '右上臂', RightForeArm: '右前臂', RightHand: '右手掌',
  LeftUpLeg: '左大腿', LeftLeg: '左小腿', LeftFoot: '左脚', LeftToeBase: '左脚趾', LeftToe_End: '左趾端',
  RightUpLeg: '右大腿', RightLeg: '右小腿', RightFoot: '右脚', RightToeBase: '右脚趾', RightToe_End: '右趾端',
};
const FINGER_ZH: Record<string, string> = {
  Thumb: '拇指', Index: '食指', Middle: '中指', Ring: '无名指', Pinky: '小指',
};

/** 骨骼名→中文短标签（mixamorigLeftHandIndex2 → 左·食指2） */
export function boneLabel(name: string): string {
  const rest = name.replace(/^mixamorig/, '');
  const finger = /^(Left|Right)Hand(Thumb|Index|Middle|Ring|Pinky)(\d)$/.exec(rest);
  if (finger !== null) {
    const side = finger[1] === 'Left' ? '左' : '右';
    const kind = FINGER_ZH[finger[2] ?? ''] ?? '';
    return `${side}·${kind}${finger[3] ?? ''}`;
  }
  return BONE_LABEL_ZH[rest] ?? rest;
}

function boneGroupOf(name: string): string {
  const rest = name.replace(/^mixamorig/, '');
  if (/^(Left|Right)Hand(Thumb|Index|Middle|Ring|Pinky)\d$/.test(rest)) {
    return rest.startsWith('Left') ? '左手' : '右手';
  }
  if (/^(Left)(Shoulder|Arm|ForeArm|Hand)$/.test(rest)) return '左臂';
  if (/^(Right)(Shoulder|Arm|ForeArm|Hand)$/.test(rest)) return '右臂';
  if (/^(Left)(UpLeg|Leg|Foot|ToeBase|Toe_End)$/.test(rest)) return '左腿';
  if (/^(Right)(UpLeg|Leg|Foot|ToeBase|Toe_End)$/.test(rest)) return '右腿';
  return '躯干';
}

const GROUP_ORDER = ['躯干', '左臂', '左手', '右臂', '右手', '左腿', '右腿'] as const;

export interface BoneListItem {
  name: string;
  label: string;
  /** 末端骨（_End / 手指第4节）不蒙皮，旋转无可见效果→列表里标灰不可选 */
  deformable: boolean;
}
export interface BoneListGroup {
  title: string;
  bones: BoneListItem[];
}

/**
 * 是否末端骨（不参与蒙皮、旋转无任何可见效果）。
 * 实测依据：pose_library_audit 的 staticBones——13 根骨在 977 姿势里从不旋转：
 * HeadTop_End、左右 Toe_End、左右手各 5 根指头第 4 节。
 */
export function isEndBone(name: string): boolean {
  const rest = name.replace(/^mixamorig/, '');
  return rest.endsWith('_End') || /Hand(Thumb|Index|Middle|Ring|Pinky)4$/.test(rest);
}

/** 把 65 骨按肢体分组（右栏逐骨列表用）；固定组序，组内按 BONE_NAMES 原序 */
export function boneGroups(): BoneListGroup[] {
  const buckets = new Map<string, BoneListItem[]>();
  for (const name of BONE_NAMES) {
    const g = boneGroupOf(name);
    const arr = buckets.get(g);
    const item = { name, label: boneLabel(name), deformable: !isEndBone(name) };
    if (arr === undefined) {
      buckets.set(g, [item]);
    } else {
      arr.push(item);
    }
  }
  return GROUP_ORDER.filter((g) => buckets.has(g)).map((g) => ({
    title: g,
    bones: buckets.get(g)!,
  }));
}

/**
 * 全骨 rest 基底（无预设姿势时用它作 composeBones 的 preset）。
 *
 * ⚠️ 关键：这里的值必须是 **STD_FBX_REST 的欧拉角**，不是零！
 * applyBoneMap 的修正为 `q = qRest ∘ qStd⁻¹ ∘ qPose`；要让骨回到 rest（q=qRest），
 * 必须 qPose = qStd。若用零作基底，qPose=单位阵→q=qRest∘qStd⁻¹≠qRest，
 * 腿部这类 std 偏离单位阵很大的骨会直接错乱（第九轮实测：无预设拖滑杆下半身错乱）。
 * 以 std 欧拉为基底：delta=0 时合成值=std → 施加后正好回 rest；滑杆微调叠加其上。
 */
export function restBoneMap(): BoneMap {
  const out: Record<string, [number, number, number]> = {};
  const e = new Euler();
  for (const [name, q] of Object.entries(STD_FBX_REST)) {
    e.setFromQuaternion(new Quaternion(q[0], q[1], q[2], q[3]), 'XYZ');
    out[name] = [e.x, e.y, e.z];
  }
  return out;
}
